import React, { useEffect } from 'react';
import { Box, Grid, Paper, Typography, Button, Stack } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useNavigate } from 'react-router-dom';
import imgLogo from '../../assets/images/logoElecnor.webp';
import { useCountry } from '../../contexts/CountryContext';
import { getHomeRouteByRole } from '../../utils/roleRedirect';
import { Country } from '../../types/countryTypes';

/**
 * Pantalla de selección de país (opción B): tras iniciar sesión, si el usuario tiene acceso a más de
 * un país, elige aquí con cuál trabajar. Reemplaza el antiguo concepto de "Delegación".
 */
const SelectCountry: React.FC = () => {
  const navigate = useNavigate();
  const { countries, selectCountry } = useCountry();

  const getRole = (): string => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user).rols.description : '';
  };

  useEffect(() => {
    // Sin sesión no se puede seleccionar país.
    if (!localStorage.getItem('user')) {
      navigate('/', { replace: true });
      return;
    }
    // Si no hay varios países que elegir, no tiene sentido esta pantalla.
    if (countries.length === 1) {
      selectCountry(countries[0]);
      navigate(getHomeRouteByRole(getRole()), { replace: true });
    } else if (countries.length === 0) {
      navigate(getHomeRouteByRole(getRole()), { replace: true });
    }
  }, [countries, navigate, selectCountry]);

  const handleSelect = (country: Country) => {
    selectCountry(country);
    navigate(getHomeRouteByRole(getRole()), { replace: true });
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{ minHeight: '100vh', backgroundColor: '#f4f6f8' }}
    >
      <Grid item xs={11} sm={7} md={4}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box textAlign="center" mb={2}>
            <img src={imgLogo} alt="Elecnor Logo" style={{ maxWidth: '60%' }} />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" mb={1}>
            <PublicIcon sx={{ color: '#004F86' }} />
            <Typography variant="h6" sx={{ color: '#004F86' }}>
              <strong>Selecciona un país</strong>
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            Tienes acceso a varios países. Elige con cuál deseas trabajar.
          </Typography>

          <Stack spacing={1.5}>
            {countries.map((country) => (
              <Button
                key={country.id}
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => handleSelect(country)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  borderColor: '#cfd8dc',
                  color: '#263238',
                  '&:hover': { borderColor: '#0056b3', backgroundColor: '#e7f3ff' },
                }}
              >
                <Typography sx={{ fontWeight: 'bold', mr: 1 }}>{country.code}</Typography>
                {country.name}
              </Button>
            ))}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SelectCountry;
